import {Injectable} from '@angular/core';
import {FilterValues, ProviderEvent} from '../../models/vte';
import {AuthService} from '../auth.service';
import {ProviderEventListParams} from './vte-api.service';
import {OperationsFiltersService} from './operations-filters.service';
import {OperationsOrganizationService} from './operations-organization.service';
import {MetadataEntry} from './vehicle-transaction-view.service';
import {formatProviderLabel} from './vision-customer-labels';
import {VisionConfigService} from './vision-config.service';
import {ReportingDateTimeService} from '../../shared/datetime/reporting-datetime.service';

const HIDDEN_FIELD_KEYS = new Set(['raw_payload', 'raw', 'payload', 'kyra_diagnostics']);
const DIAGNOSTICS_KEY = 'kyra_diagnostics';

@Injectable({
    providedIn: 'root',
})
export class ProviderEventViewService {
    constructor(
        private visionConfig: VisionConfigService,
        private filtersService: OperationsFiltersService,
        private organizationService: OperationsOrganizationService,
        private auth: AuthService,
        private reportingDateTime: ReportingDateTimeService,
    ) {
    }

    buildListParams(
        filters: FilterValues,
        page: number,
        pageSize: number,
        ordering: string,
    ): ProviderEventListParams {
        return {
            page,
            page_size: pageSize,
            ordering: ordering || '-received_at',
            search: this.filtersService.getFilterString(filters, 'search'),
            processing_status: this.filtersService.getFilterString(filters, 'processing_status'),
            provider_id: this.filtersService.getFilterString(filters, 'provider_id'),
            camera_id: this.filtersService.getFilterString(filters, 'camera_id'),
            org_id: this.organizationService.resolveOrganizationId(
                this.filtersService.getFilterString(filters, 'org_id'),
            ),
        };
    }

    getProviderLabel(providerId: string): string {
        return formatProviderLabel(providerId, this.auth.isSuperUser());
    }

    buildMetadataEntries(event: ProviderEvent): MetadataEntry[] {
        const entries: MetadataEntry[] = [
            {key: 'id', label: 'Event ID', value: String(event.id)},
            {key: 'provider_id', label: 'Provider', value: this.getProviderLabel(event.provider_id)},
            {key: 'provider_event_id', label: 'Provider Reference', value: event.provider_event_id},
            {key: 'organization_id', label: 'Organization', value: String(event.organization_id)},
            {key: 'processing_status', label: 'Processing Status', value: event.processing_status},
        ];

        if (event.event_type) {
            entries.push({key: 'event_type', label: 'Event Type', value: event.event_type});
        }
        if (event.camera_id != null) {
            entries.push({key: 'camera_id', label: 'Camera ID', value: String(event.camera_id)});
        }
        if (event.provider_timestamp) {
            entries.push({
                key: 'provider_timestamp',
                label: 'Provider Timestamp',
                value: this.formatTimestamp(event.provider_timestamp),
            });
        }
        entries.push({
            key: 'received_at',
            label: 'Received At',
            value: this.formatTimestamp(event.received_at),
        });
        if (event.matched_at) {
            entries.push({key: 'matched_at', label: 'Matched At', value: this.formatTimestamp(event.matched_at)});
        }
        if (event.timed_out_at) {
            entries.push({key: 'timed_out_at', label: 'Timed Out At', value: this.formatTimestamp(event.timed_out_at)});
        }
        if (event.completed_at) {
            entries.push({key: 'completed_at', label: 'Completed At', value: this.formatTimestamp(event.completed_at)});
        }

        return entries;
    }

    buildNormalizedFieldEntries(event: ProviderEvent): MetadataEntry[] {
        const sources = [
            {prefix: 'normalized', data: event.normalized_fields},
            {prefix: 'parsed', data: event.parsed_fields},
            {prefix: 'hint', data: event.correlation_hints},
        ];

        const entries: MetadataEntry[] = [];
        for (const source of sources) {
            if (!source.data || typeof source.data !== 'object') {
                continue;
            }
            for (const [key, value] of Object.entries(source.data)) {
                if (HIDDEN_FIELD_KEYS.has(key.toLowerCase())) {
                    continue;
                }
                entries.push({
                    key: `${source.prefix}:${key}`,
                    label: this.humanizeKey(key),
                    value: this.stringifyValue(value),
                });
            }
        }
        return entries;
    }

    /**
     * Surfaces processing diagnostics from parsed_fields.kyra_diagnostics for operators.
     * List pages stay clean; troubleshooting stays on the detail page.
     */
    buildDiagnosticsEntries(event: ProviderEvent): MetadataEntry[] {
        const parsed = event.parsed_fields;
        if (!parsed || typeof parsed !== 'object') {
            return [];
        }
        const diagnostics = parsed[DIAGNOSTICS_KEY];
        if (!diagnostics || typeof diagnostics !== 'object') {
            return [];
        }

        const latest = (diagnostics as Record<string, unknown>)['latest'];
        const source = (latest && typeof latest === 'object'
            ? latest
            : diagnostics) as Record<string, unknown>;

        const entries: MetadataEntry[] = [];
        const outcome = source['outcome'] ?? (diagnostics as Record<string, unknown>)['outcome'];
        const reason = source['reason'] ?? (diagnostics as Record<string, unknown>)['reason'];
        const remediation = source['remediation'];
        const stage = source['stage'];
        const recordedAt = source['recorded_at'];
        const elapsedMs = source['elapsed_ms'];

        if (outcome != null && outcome !== '') {
            entries.push({key: 'outcome', label: 'Processing Outcome', value: String(outcome)});
        }
        if (reason != null && reason !== '') {
            entries.push({key: 'reason', label: 'Processing Reason', value: String(reason)});
        }
        if (remediation != null && remediation !== '') {
            entries.push({key: 'remediation', label: 'Suggested Remediation', value: String(remediation)});
        }
        if (stage != null && stage !== '') {
            entries.push({key: 'stage', label: 'Stage', value: String(stage)});
        }
        if (elapsedMs != null && elapsedMs !== '') {
            entries.push({key: 'elapsed_ms', label: 'Elapsed (ms)', value: String(elapsedMs)});
        }
        if (recordedAt != null && recordedAt !== '') {
            entries.push({
                key: 'recorded_at',
                label: 'Recorded At',
                value: this.formatTimestamp(String(recordedAt)),
            });
        }

        return entries;
    }

    buildTimeline(event: ProviderEvent): Array<{ title: string; created_at: string | null }> {
        const activities = [
            {title: 'Event received', created_at: event.received_at},
        ];
        if (event.matched_at) {
            activities.push({title: 'Matched to transaction', created_at: event.matched_at});
        }
        if (event.timed_out_at) {
            activities.push({title: 'Correlation timed out', created_at: event.timed_out_at});
        }
        if (event.completed_at) {
            activities.push({title: 'Processing completed', created_at: event.completed_at});
        }
        return activities;
    }

    private formatTimestamp(value: string | null | undefined): string {
        if (!value) {
            return '—';
        }
        const formatted = this.reportingDateTime.format(value);
        return formatted || value;
    }

    private humanizeKey(key: string): string {
        return key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
    }

    private stringifyValue(value: unknown): string {
        if (value === null || value === undefined) {
            return '—';
        }
        if (typeof value === 'object') {
            return JSON.stringify(value);
        }
        return String(value);
    }
}
